import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { isAdmin, isAdminVerified } from "@/hooks/useIsAdmin";
import { LoadingScreen } from "./LoadingScreen";

export function RequireAuth() {
  const { session, loading } = useSession();
  const loc = useLocation();
  const [archivedCheck, setArchivedCheck] = useState<"unknown" | "active" | "archived">("unknown");

  // Defense-in-depth: archived users are auth-banned, so they shouldn't be
  // able to sign in. But if an active session is open at the moment an admin
  // archives them, the JWT stays valid until refresh. Check patients.archived_at
  // on mount; sign out + bounce if archived.
  useEffect(() => {
    if (!session?.user.id) { setArchivedCheck("unknown"); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("patients")
        .select("archived_at")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data?.archived_at) {
        setArchivedCheck("archived");
        await supabase.auth.signOut();
      } else {
        setArchivedCheck("active");
      }
    })();
    return () => { cancelled = true; };
  }, [session?.user.id]);

  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (archivedCheck === "archived") {
    return <Navigate to="/login" replace state={{ archived: true }} />;
  }
  // Block render until we've confirmed the user isn't archived. This is a single
  // round trip on first mount; subsequent navigations reuse the active state.
  if (archivedCheck === "unknown") return <LoadingScreen />;
  return <Outlet />;
}

export function RequireGuest() {
  const { session, loading } = useSession();
  if (loading) return <LoadingScreen />;
  if (session) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// Admin surface gate. Three states once the user is signed in with the
// admin role:
//   1. aal2 → render the admin route.
//   2. aal1 + no verified TOTP factor → /admin/mfa/setup to enroll.
//   3. aal1 + verified factor exists  → /admin/mfa/challenge to enter the code.
// We look up factors with the Supabase MFA helper since "do they have a
// factor enrolled?" isn't in the JWT.
export function RequireAdmin() {
  const { session, loading } = useSession();
  const loc = useLocation();
  const [hasFactor, setHasFactor] = useState<boolean | null>(null);

  // Only fetch factors if the user is admin-role + not yet aal2; saves a
  // round-trip on every protected page load for the verified happy path.
  const needsCheck = !!session && isAdmin(session) && !isAdminVerified(session);

  useEffect(() => {
    if (!needsCheck) { setHasFactor(null); return; }
    let cancelled = false;
    supabase.auth.mfa.listFactors().then(({ data }) => {
      if (cancelled) return;
      const verified = data?.totp?.some((f) => f.status === "verified") ?? false;
      setHasFactor(verified);
    });
    return () => { cancelled = true; };
  }, [needsCheck]);

  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  if (!isAdmin(session)) return <Navigate to="/admin/login" replace state={{ notAdmin: true }} />;
  if (isAdminVerified(session)) return <Outlet />;

  // aal1 admin — figure out enroll vs challenge.
  if (hasFactor === null) return <LoadingScreen label="Checking your authenticator…" />;
  return <Navigate to={hasFactor ? "/admin/mfa/challenge" : "/admin/mfa/setup"} replace />;
}
